import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, LinearProgress, Stack, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { AppPage, AppSection, FigureRow } from '../../components/app/AppPage';
import { ActivationChecklist } from '../../components/onboarding/ActivationChecklist';
import { ApiActivation, OnboardingStatus, activationOf, onboardingService } from '../../services/onboardingService';
import { CreatedApiKey, developerService, errorMessage } from '../../services/developerService';
import { calculateSnippet, KEY_PLACEHOLDER } from '../developers/snippets';
import { copyText } from '../developers/copyText';

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const CopyButton: React.FC<{ text: string; label: string }> = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copied' : label}>
      <Button
        size="small"
        variant="outlined"
        startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
        onClick={async () => {
          if (await copyText(text)) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
          }
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </Tooltip>
  );
};

const Code: React.FC<{ code: string; label: string }> = ({ code, label }) => (
  <Box sx={{ position: 'relative', bgcolor: 'var(--color-ink)', color: 'var(--color-paper)', borderRadius: 1, p: 2, overflowX: 'auto' }}>
    <Box sx={{ position: 'absolute', top: 6, right: 6, '& .MuiButton-root': { color: 'var(--color-paper-3)', borderColor: 'var(--color-paper-3)' } }}>
      <CopyButton text={code} label={`Copy ${label}`} />
    </Box>
    <Box component="pre" sx={{ m: 0, pr: 8, fontFamily: '"Red Hat Mono", ui-monospace, monospace', fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre' }}>
      <code>{code}</code>
    </Box>
  </Box>
);

/** One page from a fresh account to a first calculation: company, tax code, key, call, dashboard. */
export const OnboardingPage: React.FC = () => {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [checked, setChecked] = useState<'idle' | 'checking' | 'none'>('idle');

  const api: ApiActivation = useMemo(() => activationOf(status), [status]);
  const testKey = createdKey?.key ?? null;
  const snippet = useMemo(() => calculateSnippet('curl', testKey || KEY_PLACEHOLDER), [testKey]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setStatus(await onboardingService.status());
    } catch (err) {
      setLoadError(errorMessage(err, 'Could not load your setup progress.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const createTestKey = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const key = await developerService.createKey('First test key', 'test');
      setCreatedKey(key);
      await load();
    } catch (err) {
      setCreateError(errorMessage(err, 'Could not create a test key.'));
    } finally {
      setCreating(false);
    }
  };

  const checkFirstCall = async () => {
    setChecked('checking');
    try {
      const next = await onboardingService.status();
      setStatus(next);
      setChecked(next.api?.first_call_done ? 'idle' : 'none');
    } catch (err) {
      setLoadError(errorMessage(err, 'Could not check for your first call.'));
      setChecked('idle');
    }
  };

  const scrollToCall = () => {
    const el = document.getElementById('first-call');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const title = api.first_call_done ? 'Your first calculation is in' : 'Get to your first calculation';

  return (
    <AppPage
      width="narrow"
      title={title}
      description={
        api.first_call_done
          ? 'The API is wired up. Everything else in GoTruTax now reads from the same engine.'
          : 'Four steps, about five minutes. Test keys are free, never billed, and safe to use in CI.'
      }
      actions={
        api.first_call_done ? (
          <Button component={RouterLink} to="/dashboard" variant="contained">Open dashboard</Button>
        ) : (
          <Button component={RouterLink} to="/developers" variant="text">Read the API docs</Button>
        )
      }
    >
      <AppSection
        title="Setup checklist"
        description="Each step reads from your account and ticks itself off."
      >
        <ActivationChecklist
          status={status}
          loading={loading}
          error={loadError}
          onRetry={load}
          overrides={{
            testkey: api.has_key ? (
              <Button component={RouterLink} to="/developers/console" size="small" variant="outlined">
                Open developer console
              </Button>
            ) : (
              <Button size="small" variant="contained" onClick={createTestKey} disabled={creating}>
                {creating ? 'Creating…' : 'Create a test key'}
              </Button>
            ),
            firstcall: api.first_call_done ? undefined : (
              <Button size="small" variant="outlined" onClick={scrollToCall}>
                {api.has_key ? 'Copy the command' : 'See the command'}
              </Button>
            ),
          }}
        />
        {createError && <Alert severity="error" sx={{ mt: 2 }}>{createError}</Alert>}
      </AppSection>

      {createdKey && (
        <AppSection
          title="Copy your test key now"
          description="It is shown once. Store it in a secret manager or an environment variable."
        >
          <Alert severity="warning" data-testid="created-key">
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Box component="code" sx={{ fontFamily: '"Red Hat Mono", ui-monospace, monospace', wordBreak: 'break-all' }} data-testid="created-key-value">
                {createdKey.key}
              </Box>
              <CopyButton text={createdKey.key} label="Copy test key" />
            </Stack>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Lost it? Revoke it in the developer console and create another; we only keep a hash.
            </Typography>
          </Alert>
        </AppSection>
      )}

      <div id="first-call">
        <AppSection
          title="Send your first calculation"
          description="Paste this in a terminal. It is a real request: a cart in Indianapolis, IN, taxed by the engine in test mode."
          action={<Chip size="small" label="Test mode" variant="outlined" />}
        >
          <Stack spacing={2}>
            {!api.has_key ? (
              <Alert severity="info">
                Create a test key first — the command needs it. Test keys never bill.
              </Alert>
            ) : !testKey ? (
              <Alert severity="info">
                Your key starts with <code>{api.key_prefix || 'gtt_test_'}…</code>. Paste the full key you copied when you
                created it, or create a new key to see it again.
              </Alert>
            ) : null}

            <Code code={snippet} label="first calculation command" />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Button
                variant="contained"
                onClick={checkFirstCall}
                disabled={checked === 'checking' || !api.has_key}
              >
                {checked === 'checking' ? 'Checking…' : 'I ran it — check now'}
              </Button>
              <Button component={RouterLink} to="/developers/console" variant="text">
                Python and Node samples
              </Button>
              <Typography variant="body2" color="text.secondary">
                Takes a second after the request returns.
              </Typography>
            </Stack>

            {checked === 'checking' && <LinearProgress aria-label="Checking for your first call" />}
            {checked === 'none' && (
              <Alert severity="warning" data-testid="no-call-yet">
                No billable call has come through yet. Check that the command used your full key and that it printed a
                JSON body, then check again.
              </Alert>
            )}

            {api.first_call_done && (
              <Alert severity="success" data-testid="first-call-done">
                <Typography fontWeight={600} gutterBottom>Calculation received.</Typography>
                <Typography variant="body2">
                  {api.calls_total.toLocaleString()} billable call{api.calls_total === 1 ? '' : 's'} this month
                  {` (${api.test_calls.toLocaleString()} test)`}. Last call {fmtDate(api.last_call_at)}.
                </Typography>
                <Button component={RouterLink} to="/dashboard" variant="contained" size="small" sx={{ mt: 1 }}>
                  See it on your dashboard
                </Button>
              </Alert>
            )}
          </Stack>
        </AppSection>
      </div>

      <AppSection title="API activity" description="Calls this month for this account.">
        <FigureRow
          items={[
            { label: 'Test calls', value: api.test_calls.toLocaleString(), note: 'Never billed' },
            { label: 'Live calls', value: api.live_calls.toLocaleString(), note: 'Count toward the free month' },
            {
              label: 'Last call',
              value: api.last_call_at ? fmtDate(api.last_call_at) : 'None yet',
              tone: api.first_call_done ? 'ok' : undefined,
            },
          ]}
        />
        {(api.first_call_done || api.has_key) && (
          <Box sx={{ mt: 2 }}>
            <Button component={RouterLink} to="/dashboard" variant="outlined" size="small">
              Open the dashboard
            </Button>
          </Box>
        )}
      </AppSection>
    </AppPage>
  );
};

export default OnboardingPage;
